import React, { useState, useEffect } from "react";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowRight,
  CheckCircle2,
  PauseCircle,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Zap,
  AlertTriangle,
  CalendarX2,
  ArrowUpDown,
  Plus,
  Minus,
  RotateCw,
} from "lucide-react";

import {
  useLazyAdminSubscriptionDashboardQuery,
  useLazyAdminSubscriptionsByIdQuery,
  useLazyAdminSubscriptionsQuery,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation,
  useAdminSubscriptionRenewMutation,
  useCancelSubscriptionMutation,
  useUpdateSubscriptionQuantityAdminMutation,
  useChangeBlilingCycleAdminMutation,
} from "../../../redux/actions/Admin/dashboardAdmin";

import { useAlert } from "../../../components/customer/AlertContext";
import { useOutletContext } from "react-router-dom";

const STATUS_MAP: Record<
  string,
  1 | 2 | 3 | 4 | 5 | undefined
> = {
  All: undefined,
  Pending: 1,
  Active: 2,
  Paused: 3,
  Cancelled: 4,
  Expired: 5,
};

const CYCLE_MAP: Record<
  string,
  1 | 2 | 3 | undefined
> = {
  All: undefined,
  Weekly: 1,
  Monthly: 2,
  Custom: 3,
};

interface Props {
  headerDate?: string | Date;
  headerFromDate?: string;
  headerToDate?: string;
}

const formatDateToISO = (
  dateVal?: string | Date
): string | undefined => {
  if (!dateVal) return undefined;

  const d = new Date(dateVal);

  if (isNaN(d.getTime())) return undefined;

  return d.toISOString().split("T")[0];
};

const AdminSubscriptionOrders: React.FC<Props> = ({
  headerDate,
  headerFromDate,
  headerToDate,
}) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [cycleFilter, setCycleFilter] = useState("All");
  const [sortBy, setSortBy] = useState<
    "newest" | "oldest"
  >("newest");

  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] =
    useState<number | null>(null);

  // ============================================================
  // EDITABLE QUANTITY
  // ============================================================

  const [editQuantity, setEditQuantity] =
    useState<number>(1);

  // ============================================================
  // EDITABLE BILLING CYCLE
  // ============================================================

  const [editBillingCycle, setEditBillingCycle] =
    useState<string>("Weekly");

  const [editInterval, setEditInterval] =
    useState<number>(1);

  const { showConfirm } = useAlert();

  const { headerDates } =
    useOutletContext<{
      viewMode: string;
      headerDates?: {
        fromDate?: string;
        toDate?: string;
      };
    }>() || {};

  const activeFromDate =
    headerFromDate ||
    formatDateToISO(headerDate) ||
    headerDates?.fromDate;

  const activeToDate =
    headerToDate ||
    formatDateToISO(headerDate) ||
    headerDates?.toDate;

  // ============================================================
  // RTK QUERY
  // ============================================================

  const [
    getStats,
    {
      data: stats,
      isFetching: loadingStats,
    },
  ] = useLazyAdminSubscriptionDashboardQuery();

  const [
    getList,
    {
      data: list,
      isFetching: loadingList,
    },
  ] = useLazyAdminSubscriptionsQuery();

  const [
    getDetails,
    {
      data: details,
      isFetching: loadingDetails,
    },
  ] = useLazyAdminSubscriptionsByIdQuery();

  const [
    pauseSubscription,
    {
      isLoading: isPausing,
    },
  ] = usePauseSubscriptionMutation();

  const [
    resumeSubscription,
    {
      isLoading: isResuming,
    },
  ] = useResumeSubscriptionMutation();

  const [
    renewSubscription,
    {
      isLoading: isRenewing,
    },
  ] = useAdminSubscriptionRenewMutation();

  const [
    cancelSubscription,
    {
      isLoading: isCancelling,
    },
  ] = useCancelSubscriptionMutation();

  const [
    updateQuantity,
    {
      isLoading: isUpdatingQty,
    },
  ] = useUpdateSubscriptionQuantityAdminMutation();

  const [
    changeBillingCycle,
    {
      isLoading: isUpdatingBilling,
    },
  ] = useChangeBlilingCycleAdminMutation();

  // ============================================================
  // LOAD DASHBOARD
  // ============================================================

  useEffect(() => {
    getStats();
  }, [getStats]);

  // ============================================================
  // LOAD SUBSCRIPTION LIST
  // ============================================================

  useEffect(() => {
    getList({
      pageNumber: page,
      pageSize: 10,
      search: search || undefined,
      status: STATUS_MAP[statusFilter],
      billingCycle: CYCLE_MAP[cycleFilter],
      fromDate: activeFromDate,
      toDate: activeToDate,
      sortBy,
    });
  }, [
    search,
    statusFilter,
    cycleFilter,
    page,
    activeFromDate,
    activeToDate,
    sortBy,
    getList,
  ]);

  // ============================================================
  // LOAD DETAILS
  // ============================================================

  useEffect(() => {
    if (selectedId !== null) {
      getDetails(selectedId);
    }
  }, [selectedId, getDetails]);

  // ============================================================
  // SYNC EDITABLE STATES
  // ============================================================

  useEffect(() => {
    if (details) {
      if (details.quantity) {
        setEditQuantity(details.quantity);
      }

      const rawCycle =
        details.billingCycleName ||
        (details.billingCycle === 2
          ? "Monthly"
          : "Weekly");

      setEditBillingCycle(rawCycle);

      setEditInterval(details.interval ?? 1);
    }
  }, [details]);

  // ============================================================
  // REFRESH
  // ============================================================

  const refreshDashboardData = (id: number) => {
    getDetails(id);

    getStats();

    getList({
      pageNumber: page,
      pageSize: 10,
      search: search || undefined,
      status: STATUS_MAP[statusFilter],
      billingCycle: CYCLE_MAP[cycleFilter],
      fromDate: activeFromDate,
      toDate: activeToDate,
      sortBy,
    });
  };

  // ============================================================
  // UPDATE QUANTITY
  // ============================================================

  const handleUpdateQuantity = async (
    id: number,
    newQty: number
  ) => {
    if (newQty < 1) return;

    try {
      await updateQuantity({
        id,
        data: {
          quantity: newQty,
        },
      }).unwrap();

      showConfirm({
        title: "Updated",
        message:
          "Subscription quantity updated successfully!",
        type: "success",
        confirmText: "OK",
      });

      refreshDashboardData(id);
    } catch (error: any) {
      setEditQuantity(details?.quantity || 1);

      showConfirm({
        title: "Error",
        message:
          error.data?.Message ||
          "Failed to update quantity.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  // ============================================================
  // UPDATE BILLING CYCLE
  // ============================================================

  const handleUpdateBillingCycle = async (
    id: number
  ) => {
    try {
      await changeBillingCycle({
        id,
        data: {
          billingCycle: editBillingCycle,
          interval: Number(editInterval),
        },
      }).unwrap();

      showConfirm({
        title: "Updated",
        message:
          "Billing cycle updated successfully!",
        type: "success",
        confirmText: "OK",
      });

      refreshDashboardData(id);
    } catch (error: any) {
      showConfirm({
        title: "Error",
        message:
          error.data?.Message ||
          "Failed to update billing cycle.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  // ============================================================
  // PAUSE / RESUME
  // ============================================================

  const handleStatusChange = async (
    id: number,
    targetStatus: "pause" | "continue"
  ) => {
    try {
      if (targetStatus === "pause") {
        await pauseSubscription(id).unwrap();
      } else {
        await resumeSubscription(id).unwrap();
      }

      refreshDashboardData(id);
    } catch (error) {
      showConfirm({
        title: "Error",
        message:
          "Failed to update subscription status.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  // ============================================================
  // MANUAL RENEW
  // ============================================================

  const handleRenewNow = async (id: number) => {
    try {
      await renewSubscription(id).unwrap();

      setSelectedId(null);

      showConfirm({
        title: "Success",
        message:
          "Subscription renewed successfully!",
        type: "success",
        confirmText: "OK",
      });

      refreshDashboardData(id);
    } catch (error: any) {
      showConfirm({
        title: "Error",
        message:
          error.data?.Message ||
          "Failed to manually renew subscription.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  // ============================================================
  // CANCEL
  // ============================================================

  const handleCancelSubscription = async (
    id: number
  ) => {
    try {
      await cancelSubscription(id).unwrap();

      setSelectedId(null);

      showConfirm({
        title: "Cancelled",
        message:
          "Subscription cancelled successfully.",
        type: "success",
        confirmText: "OK",
      });

      refreshDashboardData(id);
    } catch (error: any) {
      showConfirm({
        title: "Error",
        message:
          error.data?.Message ||
          "Failed to cancel subscription.",
        type: "error",
        confirmText: "OK",
      });
    }
  };

  // ============================================================
  // RESET FILTERS
  // ============================================================

  const resetAllFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setCycleFilter("All");
    setSortBy("newest");
    setPage(1);
  };

  // ============================================================
  // LOADING STATE
  // ============================================================

  const isMutating =
    isPausing ||
    isResuming ||
    isRenewing ||
    isCancelling ||
    isUpdatingQty ||
    isUpdatingBilling;

  const isAnyFilterActive =
    search !== "" ||
    statusFilter !== "All" ||
    cycleFilter !== "All" ||
    sortBy !== "newest";

  // ============================================================
  // BILLING COMPARISON
  // ============================================================

  const originalCycleName =
    details?.billingCycleName ||
    (details?.billingCycle === 2
      ? "Monthly"
      : "Weekly");

  const originalInterval =
    details?.interval ?? 1;

  const hasBillingCycleChanged =
    editBillingCycle !== originalCycleName ||
    Number(editInterval) !==
      Number(originalInterval);

  // ============================================================
  // SUBSCRIPTION PRICING
  // ============================================================

  const originalPrice = Number(
    details?.originalPrice || 0
  );

  const quantity = Number(
    details?.quantity || 0
  );

  const firstDiscount = Number(
    details?.firstSubscriptionDiscountPercent || 0
  );

  const secondDiscount = Number(
    details?.secondSubscriptionDiscountPercent || 0
  );

  const originalTotal =
    originalPrice * quantity;

  const firstSubscriptionAmount =
    originalTotal *
    (1 - firstDiscount / 100);

  const recurringRenewalAmount =
    originalTotal *
    (1 - secondDiscount / 100);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="min-h-screen bg-[#FAFBF8] p-3 sm:p-6 md:p-8 space-y-6 text-gray-800 w-full max-w-(screen-2xl) mx-auto">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b pb-5">

        <div>
          <h1 className="text-lg sm:text-xl font-black text-gray-950">
            Subscription Center
          </h1>

          <p className="text-[11px] sm:text-xs text-gray-500">
            Monitor plans and quickly pause or resume actions.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 w-full lg:w-auto text-xs font-bold">

          <div className="bg-white border border-[#DDE7D8] p-2.5 sm:p-3 rounded-xl flex flex-col justify-center">
            <span className="text-gray-400 block text-[9px] sm:text-[10px] uppercase truncate">
              Active
            </span>

            <span className="text-emerald-600 text-sm sm:text-base truncate">
              {loadingStats
                ? "..."
                : stats?.activeSubscriptions}
            </span>
          </div>

          <div className="bg-white border border-[#DDE7D8] p-2.5 sm:p-3 rounded-xl flex flex-col justify-center">

            <span className="text-gray-400 block text-[9px] sm:text-[10px] uppercase truncate">
              Paused
            </span>

            <span className="text-amber-500 text-sm sm:text-base truncate">
              {loadingStats
                ? "..."
                : stats?.pausedSubscriptions}
            </span>

          </div>

          <div className="bg-white border border-[#DDE7D8] p-2.5 sm:p-3 rounded-xl flex flex-col justify-center col-span-1 lg:min-w-[130px]">

            <span className="text-gray-400 block text-[9px] sm:text-[10px] uppercase truncate">
              Est. Revenue
            </span>

            <span className="text-[#2F5E2E] text-sm sm:text-base truncate">
              AED{" "}
              {loadingStats
                ? "..."
                : stats?.estimatedMonthlyRevenue?.toFixed(
                    0
                  )}
            </span>

          </div>

        </div>
      </div>

      {/* ========================================================
          FILTERS
      ======================================================== */}

      <div className="flex flex-col gap-4 bg-white p-3.5 rounded-xl border border-[#DDE7D8]">

        <div className="flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-end">

          <div className="flex flex-wrap items-end gap-3 w-full xl:w-auto">

            {/* STATUS */}

            <div>
              <label className="text-[9px] text-gray-400 font-bold uppercase block mb-1">
                Status
              </label>

              <div className="bg-gray-100 p-0.5 rounded-lg flex gap-0.5 text-[11px] sm:text-xs font-bold overflow-x-auto whitespace-nowrap scrollbar-none">

                {[
                  "All",
                  "Active",
                  "Paused",
                  "Cancelled",
                ].map((s) => (
                  <button
                    key={`status-btn-${s}`}
                    onClick={() => {
                      setStatusFilter(s);
                      setPage(1);
                    }}
                    className={`px-2.5 sm:px-3 py-1 rounded-md transition-all ${
                      statusFilter === s
                        ? "bg-[#2F5E2E] text-white shadow-xs"
                        : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {s}
                  </button>
                ))}

              </div>
            </div>

            {/* CYCLE */}

            <div>
              <label className="text-[9px] text-gray-400 font-bold uppercase block mb-1">
                Cycle
              </label>

              <div className="bg-gray-100 p-0.5 rounded-lg flex gap-0.5 text-[11px] sm:text-xs font-bold overflow-x-auto whitespace-nowrap scrollbar-none">

                {[
                  "All",
                  "Weekly",
                  "Monthly",
                  "Custom",
                ].map((c) => (
                  <button
                    key={`cycle-btn-${c}`}
                    onClick={() => {
                      setCycleFilter(c);
                      setPage(1);
                    }}
                    className={`px-2.5 sm:px-3 py-1 rounded-md transition-all ${
                      cycleFilter === c
                        ? "bg-[#2F5E2E] text-white shadow-xs"
                        : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {c}
                  </button>
                ))}

              </div>
            </div>

            {/* SORT */}

            <div>
              <label className="text-[9px] text-gray-400 font-bold uppercase block mb-1">
                Sort Order
              </label>

              <div className="relative flex items-center">

                <ArrowUpDown className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 pointer-events-none" />

                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(
                      e.target.value as
                        | "newest"
                        | "oldest"
                    );
                    setPage(1);
                  }}
                  className="bg-[#FAFBF8] border border-[#DDE7D8] text-gray-700 font-bold text-[11px] sm:text-xs rounded-xl pl-8 pr-8 py-1.5 focus:outline-hidden appearance-none cursor-pointer hover:border-[#2F5E2E] transition-all"
                >
                  <option value="newest">
                    Newest First
                  </option>

                  <option value="oldest">
                    Oldest First
                  </option>
                </select>

                <ChevronRight className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 rotate-90 pointer-events-none" />

              </div>
            </div>

          </div>

          {/* SEARCH */}

          <div className="relative flex items-center w-full xl:w-72 mt-2 xl:mt-0">

            <Search className="w-4 h-4 text-gray-400 absolute left-3" />

            <input
              type="text"
              placeholder="Search customer, product..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 bg-[#FAFBF8] border border-[#DDE7D8] rounded-xl text-xs focus:outline-hidden"
            />

          </div>

        </div>
      </div>

      {/* ========================================================
          SUBSCRIPTION LIST
      ======================================================== */}

      <div className="space-y-3 sm:space-y-4">

        {loadingList ? (
          <div className="bg-white rounded-xl border border-[#DDE7D8] p-12 text-center text-xs text-gray-500 flex items-center justify-center gap-2">

            <RefreshCw className="w-4 h-4 animate-spin text-[#2F5E2E]" />

            Loading database...

          </div>
        ) : !list ||
          !list.data ||
          list.data.length === 0 ? (

          <div className="bg-white rounded-xl border border-[#DDE7D8] p-8 sm:p-12 text-center flex flex-col items-center justify-center space-y-3">

            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
              <CalendarX2 className="w-6 h-6" />
            </div>

            <div>

              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                No Data Found
              </h3>

              <p className="text-xs text-gray-400 mt-1 max-w-sm">
                No subscription records match your selected criteria.
              </p>

            </div>

            {isAnyFilterActive && (
              <button
                onClick={resetAllFilters}
                className="mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition cursor-pointer"
              >
                Reset All Filters
              </button>
            )}

          </div>

        ) : (

          list.data.map((sub, index) => {

            const uiStatus = sub.status;

            const uiCycle =
              sub.billingCycleName ||
              (sub.billingCycle === 1
                ? "Weekly"
                : sub.billingCycle === 2
                ? "Monthly"
                : "Custom");

            return (
              <div
                key={
                  sub.subscriptionId
                    ? `sub-${sub.subscriptionId}-${index}`
                    : `sub-idx-${index}`
                }
                className="bg-white border border-[#DDE7D8] rounded-xl overflow-hidden flex flex-col shadow-xs transition hover:shadow-md"
              >

                <div className="bg-[#FAFBF8] border-b border-[#DDE7D8]/60 px-3 sm:px-4 py-2 flex justify-between items-center text-[11px] sm:text-xs">

                  <span className="font-bold text-[#2F5E2E]">
                    ID: #{sub.orderNumber}
                  </span>

                  <span
                    className={`px-2 py-0.5 rounded-full border text-[10px] sm:text-[11px] font-bold ${
                      sub.status === 2
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : sub.status === 3
                        ? "bg-amber-50 text-amber-700 border-amber-100"
                        : "bg-gray-50 text-gray-700 border-gray-200"
                    }`}
                  >
                    {uiStatus}
                  </span>

                </div>

                <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 md:gap-4 items-center text-[11px] sm:text-xs">

                  <div className="lg:col-span-4 min-w-0">

                    <h3 className="font-bold text-gray-900 text-xs sm:text-sm truncate">
                      {sub.customerName || "Customer"}
                    </h3>

                    <p className="text-gray-500 truncate">
                      {sub.email}
                    </p>

                  </div>

                  <div className="lg:col-span-4 border-gray-100 lg:border-x lg:px-4 min-w-0">

                    <p className="font-bold text-gray-800 truncate text-xs sm:text-sm">
                      {sub.productName}
                    </p>

                    <p className="text-gray-400 mt-0.5">
                      {uiCycle} | Qty: {sub.quantity}
                    </p>

                  </div>

                  <div className="lg:col-span-2 flex items-center md:justify-start">

                    <div className="flex items-center gap-1 text-gray-600 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 md:border-0 md:bg-transparent">

                      <CalendarIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />

                      <span>
                        Next:{" "}
                        {sub.nextBillingDate
                          ? new Date(
                              sub.nextBillingDate
                            ).toLocaleDateString()
                          : "—"}
                      </span>

                    </div>

                  </div>

                  <div className="lg:col-span-2 flex flex-row lg:flex-col items-center lg:items-end justify-between lg:justify-center border-t border-gray-50 pt-2 lg:pt-0 lg:border-t-0">

                    <div className="text-left lg:text-right">

                      <span className="text-gray-400 text-[9px] sm:text-[10px] block">
                        Estimated Total
                      </span>

                      <span className="font-black text-[#2F5E2E] text-xs sm:text-sm">
                        AED{" "}
                        {sub.estimatedAmount?.toFixed(
                          2
                        )}
                      </span>

                    </div>

                    <button
                      onClick={() =>
                        setSelectedId(
                          sub.subscriptionId
                        )
                      }
                      className="text-[#2F5E2E] font-bold flex items-center gap-0.5 hover:underline text-xs cursor-pointer"
                    >
                      Manage{" "}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>

                  </div>

                </div>

              </div>
            );
          })
        )}

      </div>

      {/* ========================================================
          PAGINATION
      ======================================================== */}

      {list && list.totalRecords > 10 && (
        <div className="flex flex-row items-center justify-between bg-white border p-3 rounded-xl text-[11px] sm:text-xs text-gray-500">

          <span>
            Page <strong>{page}</strong> of{" "}
            <strong>
              {Math.ceil(
                list.totalRecords / 10
              )}
            </strong>
          </span>

          <div className="flex gap-1">

            <button
              onClick={() =>
                setPage((p) =>
                  Math.max(p - 1, 1)
                )
              }
              disabled={page === 1}
              className="p-1.5 border rounded-lg cursor-pointer disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <button
              onClick={() =>
                setPage((p) =>
                  page * 10 <
                  list.totalRecords
                    ? p + 1
                    : p
                )
              }
              disabled={
                page * 10 >=
                list.totalRecords
              }
              className="p-1.5 border rounded-lg cursor-pointer disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

          </div>
        </div>
      )}

      {/* ========================================================
          DETAILS DRAWER
      ======================================================== */}

      {selectedId !== null && (
        <div className="fixed inset-0 bg-black/40 z-[999] flex justify-end xl:mr-[448px] animate-in fade-in duration-200">

          <div className="bg-white w-full sm:max-w-md h-full fixed top-0 right-0 bottom-0 shadow-2xl flex flex-col justify-between overflow-hidden z-[1000]">

            {loadingDetails ? (

              <div className="h-full flex items-center justify-center text-xs text-gray-500">

                <RefreshCw className="w-5 h-5 animate-spin mr-2 text-[#2F5E2E]" />

                Loading details...

              </div>

            ) : details ? (

              <div className="flex flex-col h-full overflow-hidden">

                {/* ==================================================
                    DRAWER HEADER
                ================================================== */}

                <div className="p-4 sm:p-5 flex justify-between items-center border-b shrink-0 bg-white">

                  <div>

                    <h2 className="text-sm sm:text-base font-black text-gray-900">
                      Manage Subscription
                    </h2>

                    <p className="text-gray-400 text-[10px] sm:text-[11px]">
                      Ref: #{details.orderNumber}
                    </p>

                  </div>

                  <button
                    onClick={() =>
                      setSelectedId(null)
                    }
                    disabled={isMutating}
                    className="p-2 hover:bg-gray-100 rounded-lg transition cursor-pointer"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>

                </div>

                {/* ==================================================
                    DRAWER CONTENT
                ================================================== */}

                <div className="p-4 sm:p-5 flex-1 overflow-y-auto space-y-4 text-[11px] sm:text-xs min-h-0">

                  {/* ==================================================
                      CONTROLS
                  ================================================== */}

                  <div className="bg-gray-50 border border-gray-200 p-3.5 sm:p-4 rounded-xl space-y-3">

                    <div className="flex justify-between items-center w-full">

                      <span className="font-bold text-gray-700 text-[9px] sm:text-[10px] uppercase">
                        Controls
                      </span>

                      <span
                        className={`text-[9px] sm:text-[10px] px-2 py-0.5 rounded font-bold ${
                          details.status ===
                          "Active"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {details.statusName ||
                          (details.status ===
                          "Active"
                            ? "Active"
                            : "Paused")}
                      </span>

                    </div>

                    {details.status === "Active" ? (

                      <button
                        onClick={() =>
                          handleStatusChange(
                            details.subscriptionId,
                            "pause"
                          )
                        }
                        disabled={isMutating}
                        className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition text-xs shadow-xs cursor-pointer"
                      >

                        {isPausing ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <PauseCircle className="w-4 h-4" />
                        )}

                        Pause Subscription

                      </button>

                    ) : details.status ===
                      "Paused" ? (

                      <button
                        onClick={() =>
                          handleStatusChange(
                            details.subscriptionId,
                            "continue"
                          )
                        }
                        disabled={isMutating}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition text-xs shadow-xs cursor-pointer"
                      >

                        {isResuming ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4" />
                        )}

                        Resume Subscription

                      </button>

                    ) : (

                      <div className="text-center p-2 bg-gray-100 text-gray-500 border rounded-lg font-bold text-[11px]">
                        No active transitions available.
                      </div>

                    )}

                  </div>

                  {/* ==================================================
                      PRODUCT + PRICING
                  ================================================== */}

                  <div className="border p-3 sm:p-4 rounded-xl flex flex-col gap-4 bg-white shadow-2xs">

                    {/* PRODUCT */}

                    <div className="flex gap-3">

                      {details.imageUrl && (
                        <img
                          src={details.imageUrl}
                          alt={
                            details.productName ||
                            "Product"
                          }
                          className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded border shrink-0 bg-gray-50"
                        />
                      )}

                      <div className="min-w-0 flex-1">

                        <h4 className="font-bold text-gray-900 line-clamp-2 text-[11px] sm:text-xs leading-tight">
                          {details.productName}
                        </h4>

                        <p className="text-gray-400 mt-0.5 text-[10px] sm:text-[11px] truncate">
                          {details.variantName ||
                            "Default Variant"}
                        </p>

                      </div>

                    </div>

                    {/* ==================================================
                        SUBSCRIPTION PRICING
                    ================================================== */}

                    <div className="border-t pt-3 space-y-2">

                      {/* ORIGINAL PRICE */}

                      <div className="flex justify-between items-center">

                        <span className="text-gray-500">
                          Original Price
                        </span>

                        <span className="font-bold text-gray-900">
                          AED{" "}
                          {originalPrice.toFixed(
                            2
                          )}
                        </span>

                      </div>

                      {/* QUANTITY */}

                      <div className="flex justify-between items-center">

                        <span className="text-gray-500">
                          Quantity
                        </span>

                        <span className="font-bold text-gray-900">
                          {quantity}
                        </span>

                      </div>


            

                      {/* RECURRING AMOUNT */}

                      <div className="flex justify-between items-center border-t pt-2">

                        <span className="font-bold text-gray-700">
                          Recurring Renewal Amount ({secondDiscount.toFixed(2)}%)
                        </span>

                        <span className="font-black text-[#2F5E2E] text-sm">
                          AED{" "}
                          {recurringRenewalAmount.toFixed(
                            2
                          )}
                        </span>

                      </div>

                    </div>

                    {/* ==================================================
                        QUANTITY
                    ================================================== */}

                    <div className="pt-3 border-t flex items-center justify-between">

                      <span className="text-gray-500 font-semibold text-[11px]">
                        Quantity:
                      </span>

                      <div className="flex items-center gap-2">

                        <div className="flex items-center border rounded-lg overflow-hidden bg-gray-50">

                          <button
                            type="button"
                            onClick={() =>
                              setEditQuantity(
                                (q) =>
                                  Math.max(
                                    1,
                                    q - 1
                                  )
                              )
                            }
                            disabled={
                              editQuantity <= 1 ||
                              isMutating
                            }
                            className="p-1.5 hover:bg-gray-200 disabled:opacity-40 transition cursor-pointer"
                          >
                            <Minus className="w-3.5 h-3.5 text-gray-600" />
                          </button>

                          <input
                            type="number"
                            min="1"
                            value={editQuantity}
                            onChange={(e) =>
                              setEditQuantity(
                                Math.max(
                                  1,
                                  parseInt(
                                    e.target.value
                                  ) || 1
                                )
                              )
                            }
                            disabled={isMutating}
                            className="w-10 text-center font-bold text-xs bg-transparent focus:outline-hidden"
                          />

                          <button
                            type="button"
                            onClick={() =>
                              setEditQuantity(
                                (q) => q + 1
                              )
                            }
                            disabled={isMutating}
                            className="p-1.5 hover:bg-gray-200 disabled:opacity-40 transition cursor-pointer"
                          >
                            <Plus className="w-3.5 h-3.5 text-gray-600" />
                          </button>

                        </div>

                        {editQuantity !==
                          details.quantity && (
                          <button
                            onClick={() =>
                              handleUpdateQuantity(
                                details.subscriptionId,
                                editQuantity
                              )
                            }
                            disabled={isMutating}
                            className="px-2.5 py-1 bg-[#2F5E2E] hover:bg-[#234722] text-white text-[10px] font-bold rounded-lg transition disabled:opacity-50 cursor-pointer flex items-center gap-1"
                          >

                            {isUpdatingQty && (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            )}

                            Save

                          </button>
                        )}

                      </div>

                    </div>

                  </div>

                  {/* ==================================================
                      BILLING SCHEDULE
                  ================================================== */}

                  <div className="border rounded-xl p-3.5 space-y-3 bg-[#FAFBF8]">

                    <div className="flex items-center justify-between">

                      <span className="font-bold text-gray-700 text-[10px] uppercase">
                        Billing Schedule
                      </span>

                      {hasBillingCycleChanged && (
                        <button
                          type="button"
                          onClick={() =>
                            handleUpdateBillingCycle(
                              details.subscriptionId
                            )
                          }
                          disabled={isMutating}
                          className="px-2.5 py-1 bg-[#2F5E2E] hover:bg-[#234722] text-white text-[10px] font-bold rounded-lg transition disabled:opacity-50 cursor-pointer flex items-center gap-1"
                        >

                          {isUpdatingBilling ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCw className="w-3 h-3" />
                          )}

                          Save Cycle

                        </button>
                      )}

                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">

                      <div className="space-y-1">

                        <label className="text-[10px] text-gray-500 font-semibold block">
                          Cycle
                        </label>

                        <select
                          value={
                            editBillingCycle
                          }
                          onChange={(e) =>
                            setEditBillingCycle(
                              e.target.value
                            )
                          }
                          disabled={isMutating}
                          className="w-full bg-white border border-[#DDE7D8] text-gray-800 font-bold text-xs rounded-lg px-2.5 py-1.5 focus:outline-hidden cursor-pointer"
                        >

                          <option value="Weekly">
                            Weekly
                          </option>

                          <option value="Monthly">
                            Monthly
                          </option>

                        </select>

                      </div>

                      <div className="space-y-1">

                        <label className="text-[10px] text-gray-500 font-semibold block">
                          Interval
                        </label>

                        <input
                          type="number"
                          min="0"
                          value={editInterval}
                          onChange={(e) =>
                            setEditInterval(
                              Math.max(
                                0,
                                parseInt(
                                  e.target.value
                                ) || 0
                              )
                            )
                          }
                          disabled={isMutating}
                          className="w-full bg-white border border-[#DDE7D8] text-gray-800 font-bold text-xs rounded-lg px-2.5 py-1.5 focus:outline-hidden"
                          placeholder="e.g. 0 or 1"
                        />

                      </div>

                    </div>

                    <div className="pt-2 border-t border-gray-200/60 space-y-1 text-[11px]">

                      <div className="flex justify-between text-gray-600">

                        <span>
                          Lifetime Revenue:
                        </span>

                        <strong className="text-emerald-700 font-bold">
                          AED{" "}
                          {Number(
                            details.lifetimeRevenue ||
                              0
                          ).toFixed(2)}
                        </strong>

                      </div>

                      <div className="flex justify-between text-gray-600">

                        <span>
                          Next Invoice Run:
                        </span>

                        <span className="font-bold text-gray-800">

                          {details.nextBillingDate
                            ? new Date(
                                details.nextBillingDate
                              ).toLocaleDateString()
                            : "—"}

                        </span>

                      </div>

                    </div>

                  </div>

                  {/* ==================================================
                      RENEWAL HISTORY
                  ================================================== */}

                  <div className="space-y-1.5">

                    <p className="text-gray-400 font-bold text-[9px] sm:text-[10px] uppercase">
                      Renewal History (
                      {details.renewals?.length ||
                        0}
                      )
                    </p>

                    <div className="border rounded-xl max-h-40 overflow-y-auto divide-y bg-white">

                      {details.renewals?.map(
                        (r: any, rIdx: number) => (
                          <div
                            key={
                              r.renewalId
                                ? `renewal-${r.renewalId}-${rIdx}`
                                : `renewal-idx-${rIdx}`
                            }
                            className="p-2.5 flex justify-between items-center text-[10px] sm:text-[11px]"
                          >

                            <div className="min-w-0 pr-2">

                              <p className="font-bold text-gray-800 truncate">
                                Order #{r.orderId}
                              </p>

                              <p className="text-gray-400 text-[9px] sm:text-[10px]">
                                {r.billingDate
                                  ? new Date(
                                      r.billingDate
                                    ).toLocaleDateString()
                                  : "—"}
                              </p>

                              <p className="text-[9px] text-gray-400">
                                {r.triggerName ||
                                  r.trigger ||
                                  "—"}
                              </p>

                            </div>

                            <div className="text-right shrink-0">

                              <span className="font-bold text-gray-900 block">
                                AED{" "}
                                {Number(
                                  r.amount || 0
                                ).toFixed(2)}
                              </span>

                              <span
                                className={`text-[9px] px-1 rounded font-bold inline-block ${
                                  r.paymentSucceeded
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-rose-100 text-rose-800"
                                }`}
                              >
                                {r.statusName ||
                                  r.status}
                              </span>

                            </div>

                          </div>
                        )
                      )}

                    </div>

                  </div>

                </div>

                {/* ==================================================
                    FOOTER ACTIONS
                ================================================== */}

                {details.status !==
                  "Cancelled" && (
                  <div className="p-4 border-t bg-slate-50/90 shrink-0 space-y-2 mb-safe">

                    <button
                      onClick={() =>
                        handleRenewNow(
                          details.subscriptionId
                        )
                      }
                      disabled={isMutating}
                      className="w-full py-2.5 bg-[#2F5E2E] hover:bg-[#234722] text-white text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-50 shadow-xs cursor-pointer"
                    >

                      {isRenewing ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Zap className="w-3.5 h-3.5" />
                      )}

                      Renew Plan Now

                    </button>

                    <button
                      onClick={() =>
                        handleCancelSubscription(
                          details.subscriptionId
                        )
                      }
                      disabled={isMutating}
                      className="w-full py-2.5 bg-white border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-bold rounded-xl flex items-center justify-center gap-2 transition disabled:opacity-50 cursor-pointer"
                    >

                      {isCancelling ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <AlertTriangle className="w-3.5 h-3.5" />
                      )}

                      Terminate Subscription

                    </button>

                  </div>
                )}

              </div>

            ) : null}

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminSubscriptionOrders;